import {
	ButtonType,
	InputSwitch,
	InputText,
	RelayPageDataTable,
} from "@constellation-academy/epic-ui";
import moment from "moment-timezone";
import { Calendar } from "primereact/calendar";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { useCallback, useState } from "react";
import { Button, ButtonVariant } from "@components/button";
import { DateTimeDisplay } from "@components/datetime-display/datetime-display.component";
import { IdDisplayField } from "@components/id-display-field";
import { OrderIdDisplay } from "@features/orders/order-id-display";
import { useTranslation } from "@i18n/hooks";
import { type ordersTable_OrderFragment$key } from "@relay/ordersTable_OrderFragment.graphql";
import { type ordersTable_OrdersFragment$key } from "@relay/ordersTable_OrdersFragment.graphql";
import { type ordersTable_OrdersFragmentRefetch } from "@relay/ordersTable_OrdersFragmentRefetch.graphql";
import { ORDER_FRAGMENT, ORDERS_FRAGMENT } from "./orders-table.graphql";
import {
	accountBadgeClass,
	actionButtonsWrapperClass,
	claimedGuestBadgeClass,
	noBuyerYetBadgeClass,
	customRangeFieldClass,
	customRangeFieldsRowClass,
	dateFilterControlsRowClass,
	dateFilterGroupClass,
	dateFilterLabelClass,
	dialogFooterClass,
	filterControlStyle,
	filterWrapperClass,
	guestBadgeClass,
	switchScaleWrapperClass,
} from "./orders-table.styles";
import { OrdersTableFiltersState, type OrdersTableProps } from "./orders-table.types";
import { isUnclaimedGuestOrder } from "@features/orders/unclaimed-guest-order";
import { EditOrderButton } from "../edit-order-button/edit-order-button.component";
import { OrderPriceDisplay } from "../order-price-display/order-price-display.component";
import { PaymentProviderLink } from "../payment-provider-link/payment-provider-link.component";

// Simple presets over the raw filterByCreatedAtFrom/To pair, plus a Custom option that
// reveals two date pickers - deliberately not replicating a full nested date-range menu here.
enum DateFilterPreset {
	AllTime = "AllTime",
	Last24Hours = "Last24Hours",
	Last3Days = "Last3Days",
	Last7Days = "Last7Days",
	Last30Days = "Last30Days",
	Custom = "Custom",
}

const PRESET_TO_DAYS: Partial<Record<DateFilterPreset, number>> = {
	[DateFilterPreset.Last24Hours]: 1,
	[DateFilterPreset.Last3Days]: 3,
	[DateFilterPreset.Last7Days]: 7,
	[DateFilterPreset.Last30Days]: 30,
};

export const OrdersTable = ({
	ordersFragmentRef,
	ordersPerPage,
	enableFilter,
	canModifyOrders,
}: OrdersTableProps) => {
	const { t } = useTranslation("common");

	// RelayPageDataTable manages its own pagination fragment internally and refetches on its
	// own (debounced) whenever the `filters` prop below changes - it never reads a `value` prop
	// from us at all. So a handler here only needs to update filterState; there's no separate
	// "apply" or manual refetch step, and the removed "Apply Filter" button never did anything
	// beyond that automatic behavior in the first place (nor did the manual refetch calls this
	// component used to make on its own, now-removed, unused usePaginationFragment instance -
	// that was a second, wasted network request every time a filter changed).
	const [filterState, setFilterState] = useState<OrdersTableFiltersState>({
		username: undefined,
		email: undefined,
		guestOnly: undefined,
		awaitingAccountOnly: undefined,
	});
	const [dateFilterPreset, setDateFilterPreset] = useState<DateFilterPreset>(
		DateFilterPreset.AllTime,
	);
	const [isCustomRangeDialogOpen, setCustomRangeDialogOpen] = useState(false);
	const [draftDateFrom, setDraftDateFrom] = useState<Date | undefined>(undefined);
	const [draftDateTo, setDraftDateTo] = useState<Date | undefined>(undefined);

	const handleUsernameOnChange = useCallback((value?: string) => {
		setFilterState((prevFilterState) => ({ ...prevFilterState, username: value }));
	}, []);

	const handleEmailOnChange = useCallback((value?: string) => {
		setFilterState((prevFilterState) => ({ ...prevFilterState, email: value }));
	}, []);

	const handleGuestOnlyOnChange = useCallback((checked: boolean) => {
		setFilterState((prevFilterState) => ({
			...prevFilterState,
			guestOnly: checked || undefined,
		}));
	}, []);

	// Scoped to the actionable DXP-696 case: paid as a guest and still unclaimed (status ===
	// PurchasedAwaitingAccount). Narrower than guestOnly, which also includes guest carts that
	// never completed payment.
	const handleAwaitingAccountOnlyOnChange = useCallback((checked: boolean) => {
		setFilterState((prevFilterState) => ({
			...prevFilterState,
			awaitingAccountOnly: checked || undefined,
		}));
	}, []);

	// Custom opens a dialog instead of applying immediately: the range isn't set on filterState
	// until the person explicitly hits Apply in there.
	const handleDateFilterPresetOnChange = useCallback(
		(preset: DateFilterPreset) => {
			if (preset === DateFilterPreset.Custom) {
				setDraftDateFrom(
					filterState.filterByCreatedAtFrom
						? moment(filterState.filterByCreatedAtFrom).toDate()
						: undefined,
				);
				setDraftDateTo(
					filterState.filterByCreatedAtTo
						? moment(filterState.filterByCreatedAtTo).toDate()
						: undefined,
				);
				setCustomRangeDialogOpen(true);
				return;
			}
			setDateFilterPreset(preset);
			const days = PRESET_TO_DAYS[preset];
			setFilterState((prevFilterState) => ({
				...prevFilterState,
				filterByCreatedAtFrom: days
					? moment().subtract(days, "days").format("YYYY-MM-DDTHH:mmZ")
					: undefined,
				filterByCreatedAtTo: undefined,
			}));
		},
		[filterState],
	);

	// Re-selecting an already-selected Dropdown option doesn't re-fire onChange, so this is the
	// way back into the dialog to tweak a range that's already applied.
	const handleEditCustomRangeOnClick = useCallback(() => {
		setDraftDateFrom(
			filterState.filterByCreatedAtFrom
				? moment(filterState.filterByCreatedAtFrom).toDate()
				: undefined,
		);
		setDraftDateTo(
			filterState.filterByCreatedAtTo
				? moment(filterState.filterByCreatedAtTo).toDate()
				: undefined,
		);
		setCustomRangeDialogOpen(true);
	}, [filterState]);

	const handleApplyCustomRangeOnClick = useCallback(() => {
		setFilterState((prevFilterState) => ({
			...prevFilterState,
			filterByCreatedAtFrom: draftDateFrom
				? moment(draftDateFrom).format("YYYY-MM-DDTHH:mmZ")
				: undefined,
			filterByCreatedAtTo: draftDateTo
				? moment(draftDateTo).format("YYYY-MM-DDTHH:mmZ")
				: undefined,
		}));
		setDateFilterPreset(DateFilterPreset.Custom);
		setCustomRangeDialogOpen(false);
	}, [draftDateFrom, draftDateTo]);

	const handleCancelCustomRangeOnClick = useCallback(() => {
		setCustomRangeDialogOpen(false);
	}, []);

	return (
		<>
			{enableFilter && (
				<div className={filterWrapperClass}>
					<InputText
						label={t("order.accountName")}
						value={filterState.username ?? undefined}
						onChange={handleUsernameOnChange}
					/>
					<InputText
						label={t("table.email")}
						value={filterState.email ?? undefined}
						onChange={handleEmailOnChange}
					/>
					<div className={switchScaleWrapperClass}>
						<InputSwitch
							label={t("order.guestOnly")}
							checked={filterState.guestOnly ?? false}
							onChange={handleGuestOnlyOnChange}
						/>
					</div>
					<div className={switchScaleWrapperClass}>
						<InputSwitch
							label={t("order.awaitingAccountOnly")}
							checked={filterState.awaitingAccountOnly ?? false}
							onChange={handleAwaitingAccountOnlyOnChange}
						/>
					</div>
					<div className={dateFilterGroupClass}>
						<label className={dateFilterLabelClass}>{t("order.dateFilter")}</label>
						<div className={dateFilterControlsRowClass}>
							<Dropdown
								style={filterControlStyle}
								value={dateFilterPreset}
								onChange={(e) => handleDateFilterPresetOnChange(e.value)}
								options={[
									{ label: t("order.allTime"), value: DateFilterPreset.AllTime },
									{
										label: t("order.last24Hours"),
										value: DateFilterPreset.Last24Hours,
									},
									{
										label: t("order.last3Days"),
										value: DateFilterPreset.Last3Days,
									},
									{
										label: t("order.last7Days"),
										value: DateFilterPreset.Last7Days,
									},
									{
										label: t("order.last30Days"),
										value: DateFilterPreset.Last30Days,
									},
									{
										label: t("order.customRange"),
										value: DateFilterPreset.Custom,
									},
								]}
							/>
							{dateFilterPreset === DateFilterPreset.Custom && (
								<Button
									variant={ButtonVariant.Textlink}
									type={ButtonType.Button}
									label={t("order.editRange")}
									onClick={handleEditCustomRangeOnClick}
								/>
							)}
						</div>
					</div>

					<Dialog
						header={t("order.customRange")}
						visible={isCustomRangeDialogOpen}
						onHide={handleCancelCustomRangeOnClick}
						style={{ width: "32rem" }}
						footer={
							<div className={dialogFooterClass}>
								<Button
									variant={ButtonVariant.Outline}
									type={ButtonType.Button}
									label={t("actions.cancel")}
									onClick={handleCancelCustomRangeOnClick}
								/>
								<Button
									type={ButtonType.Button}
									label={t("actions.apply")}
									onClick={handleApplyCustomRangeOnClick}
								/>
							</div>
						}
					>
						<div className={customRangeFieldsRowClass}>
							<div className={customRangeFieldClass}>
								<label className={dateFilterLabelClass}>
									{t("order.dateFrom")}
								</label>
								<Calendar
									inputStyle={filterControlStyle}
									showTime
									hourFormat="24"
									dateFormat="dd.mm.yy"
									value={draftDateFrom}
									onChange={(e) => setDraftDateFrom(e.value as Date)}
								/>
							</div>
							<div className={customRangeFieldClass}>
								<label className={dateFilterLabelClass}>{t("order.dateTo")}</label>
								<Calendar
									inputStyle={filterControlStyle}
									showTime
									hourFormat="24"
									dateFormat="dd.mm.yy"
									value={draftDateTo}
									onChange={(e) => setDraftDateTo(e.value as Date)}
								/>
							</div>
						</div>
					</Dialog>
				</div>
			)}

			<RelayPageDataTable<
				ordersTable_OrdersFragmentRefetch,
				ordersTable_OrdersFragment$key,
				ordersTable_OrderFragment$key
			>
				queryFragment={ORDERS_FRAGMENT}
				parentFragmentRef={ordersFragmentRef}
				nodeInlineFragment={ORDER_FRAGMENT}
				extractConnection={(data) => data.Admin.Billing.Orders}
				emptyMessage={t("order.noOrders")}
				rowsCount={ordersPerPage}
				filters={filterState}
			>
				{(Column) => (
					<>
						<Column
							header={t("table.id")}
							body={(order) => <IdDisplayField id={order.id} />}
							style={{ width: "5%" }}
						/>
						<Column
							header={t("order.creationDate")}
							body={(order) => (
								<DateTimeDisplay value={order.createdAt} hideTimezone />
							)}
						/>
						<Column
							header={t("order.status")}
							body={(order) => <div>{t(`orderStatus.${order.status}`)}</div>}
						/>
						<Column
							header={t("order.buyer")}
							// Four distinct states, because "no purchaser" alone does not mean guest
							// checkout: the admin list is not status-filtered, so in-progress carts show
							// up here too and a logged-out visitor's cart has no purchaser yet either.
							// startedAsGuest is what separates "chose guest checkout" from "nobody
							// attributed yet", and it survives being claimed - so it also distinguishes
							// a claimed guest order from an ordinary account one.
							body={(order) => {
								if (!order.purchaser) {
									return isUnclaimedGuestOrder(order) ? (
										<span className={guestBadgeClass}>{t("order.guest")}</span>
									) : (
										<span className={noBuyerYetBadgeClass}>
											{t("order.noBuyerYet")}
										</span>
									);
								}

								if (order.startedAsGuest) {
									return (
										<span className={claimedGuestBadgeClass}>
											{t("order.claimedGuest")}
										</span>
									);
								}

								return (
									<span className={accountBadgeClass}>{t("order.account")}</span>
								);
							}}
							style={{ width: "14%" }}
						/>
						<Column
							header={t("order.price")}
							body={(order) => <OrderPriceDisplay orderFragmentRef={order} />}
						/>
						<Column
							header={t("order.orderId")}
							body={(order) => <OrderIdDisplay orderFragmentRef={order} />}
						/>
						<Column
							header={t("order.paymentMethod")}
							// Merged column (Ali's call): PaymentProviderLink now owns the full fallback
							// chain (paid/pending provider details -> chosen method only -> nothing yet),
							// so this single column carries what used to be two separate ones.
							body={(order) => <PaymentProviderLink orderFragmentRef={order} />}
						/>
						{canModifyOrders && (
							<Column
								header={t("table.actions")}
								style={{ width: "10%" }}
								body={(order) => (
									<div className={actionButtonsWrapperClass}>
										<EditOrderButton orderId={order.id} />
									</div>
								)}
							/>
						)}
					</>
				)}
			</RelayPageDataTable>
		</>
	);
};
